/* eslint-disable func-style */
/* eslint-disable no-empty-function */
import { isBrowser } from '@cp/utils/browser';
import { getDeviceId, tokens } from '@cp/shared/deviceId';
import { init, setTag } from '@sentry/browser';
import { PersistConfig } from 'redux-persist';
import { MODAL_MANAGER, MODAL_STATS_PROVIDER } from '@cp/shared/modal';
import { ROUTER } from '@cp/shared/router';
import { createBrowserHistory, createMemoryHistory } from 'history';
import { CachedJSONStorage, JSONStorage, MemoryStorage } from '@cp/shared/storage';
import { Answer, NoAnswer } from 'yandex-metrica-ab-react/dist/types';
import { favoritesReducerPath } from '@cp/entities/client';
import { merge } from 'lodash';
import { CONFIG_MANAGER_TOKEN, createConfig } from 'src/app/providers/config';
import { SPIDER_SERVICE_TOKEN } from 'src/app/providers/tracking/const';
import { SpiderService } from 'src/app/providers/tracking/spiderService';
import { Container } from 'src/app/ioc';
import { RootState, STORE, configureStore } from 'src/app/redux';
import { RefModalManager } from 'src/app/providers/modal';
import { BrowserRouter, HISTORY_API, StaticRouter } from 'src/app/providers/router';
import { ClientSelectionService, SELECTION_SERVICE, SELECTION_STORAGE } from 'src/features/clientSelection';
import { CLIENT_SELECTION_GETTER, DEVICE_IDENTIFIER } from 'src/features/auth/di';
import { GET_CLIENT_SELECTIONS } from 'src/features/registration/di';
import { INTENTION_SERVICE, INTENTION_STORAGE, IntentData, IntentionService } from 'src/shared/intent';
import { SUPPORT_CODE_SERVICE, SupportCodeService } from 'src/shared/supportCode';
import { AB_SSR_VALUE } from 'src/shared/ab';
import { FAVORITES_SERVICE, FavoritesService, FavoritesStorage } from 'src/features/client/current/favorite';
import { USER_STORAGE_KEY, getUserStorageToken } from 'src/entities/user';
import { CATALOG_ITEM_STORAGE_SERVICE, CatalogItemStorage, CatalogItemStorageService } from 'src/features/catalogItem';
import { updateCoreApiOptions } from '@cp/shared/api/core';
import { WEBSOCKET_MANAGER, createSocketManager } from '@cp/shared/api/socket';
import { detectMobileDevice } from 'src/shared/tracking';
import { EVENT_BUS_TOKEN, EventBus } from './providers/eventBus';
import { registerServiceWorker } from './sw';
import { BrowserDeviceIDStorage, fingerprint, stubDIDStorage, uuidGenerator } from './providers/deviceId';

type UserStorage = FavoritesStorage & CatalogItemStorage;

export function bootstrap(
  container: Container,
  {
    config,
    redux,
    url = '/',
    varioqub,
  }: {
    config?: Record<string, unknown>;
    redux: { preloadedState?: Partial<RootState>; persistConfig: PersistConfig<RootState> };
    url?: string | URL;
    varioqub?: Answer | NoAnswer;
  },
) {
  const configManager = createConfig(config);
  const eventBus = isBrowser ? new EventBus() : { subscribe: () => {} };
  const spiderService = isBrowser ? new SpiderService(configManager) : { onMessageReceived: () => {}, spider: {} };
  const deviceIdStorage = isBrowser ? new BrowserDeviceIDStorage(localStorage) : stubDIDStorage;
  const supportCodeService = new SupportCodeService(isBrowser ? new CachedJSONStorage(sessionStorage, 'LSCode') : new MemoryStorage());
  updateCoreApiOptions({ baseUrl: configManager.getOrThrow('CORE_SERVER_ORIGIN') });

  if (isBrowser && configManager.has('SENTRY_DSN')) {
    init({
      debug: configManager.get('SENTRY_DEBUG', false),
      dsn: configManager.getOrThrow('SENTRY_DSN'),
      release: configManager.get('BUILD_ID', ''),
      environment: configManager.get('APP_ENV', 'production'),
    });
    setTag('LSCode', supportCodeService.getCode());
  }

  if (isBrowser) {
    (spiderService as SpiderService).updateContext({
      mobile: detectMobileDevice(),
      ua: navigator.userAgent ?? null,
      session_id: supportCodeService.getCode(),
    });
  }

  container.bind({ provide: AB_SSR_VALUE, useValue: varioqub || { ready: false, flags: {} } });
  container.bind({ provide: SUPPORT_CODE_SERVICE, useValue: supportCodeService });
  container.bind({
    provide: HISTORY_API,
    useValue: isBrowser ? createBrowserHistory() : createMemoryHistory({ initialEntries: [String(url)] }),
  });
  container.bind({
    provide: ROUTER,
    useValue: isBrowser
      ? new BrowserRouter(configManager, container.get(HISTORY_API))
      : new StaticRouter(new URL(url, configManager.getOrThrow('CLIENT_WEB_ORIGIN'))),
  });
  container.bind({ provide: EVENT_BUS_TOKEN, useValue: eventBus });
  container.bind({ provide: CONFIG_MANAGER_TOKEN, useValue: configManager });
  container.bind({ provide: SPIDER_SERVICE_TOKEN, useValue: spiderService });
  container.bind({
    provide: tokens.DEVICE_ID_GENERATOR,
    useFactory: () => (crypto && 'randomUUID' in crypto ? uuidGenerator : fingerprint),
  });
  container.bind({ provide: tokens.DEVICE_ID_STORAGE, useValue: deviceIdStorage });
  container.bind({
    provide: DEVICE_IDENTIFIER,
    useValue: () => getDeviceId(container.get(tokens.DEVICE_ID_STORAGE), container.get(tokens.DEVICE_ID_GENERATOR)),
  });
  container.bind({
    provide: SELECTION_STORAGE,
    useValue: isBrowser ? new CachedJSONStorage<any>(window.sessionStorage, 'clientSelection') : new MemoryStorage<any>(),
  });
  container.bind({
    provide: getUserStorageToken(),
    useValue: isBrowser ? new CachedJSONStorage(localStorage, USER_STORAGE_KEY) : new MemoryStorage(),
  });
  container.bind({ provide: SELECTION_SERVICE, useValue: new ClientSelectionService(container.get(SELECTION_STORAGE)) });
  container.bind({ provide: CLIENT_SELECTION_GETTER, useValue: () => Promise.resolve(container.get(SELECTION_SERVICE).getSelections()) });
  container.bind({ provide: GET_CLIENT_SELECTIONS, useValue: () => Promise.resolve(container.get(SELECTION_SERVICE).getSelections()) });

  if (isBrowser) {
    const deviceIDGetter = container.get(DEVICE_IDENTIFIER);
    deviceIDGetter().then((device_id) => {
      (spiderService as SpiderService).updateContext({ device_id });
    });
  }
  const userStorage = container.get(getUserStorageToken<UserStorage>());
  const favoritesService = new FavoritesService(userStorage);

  container.bind({ provide: FAVORITES_SERVICE, useValue: favoritesService });
  container.bind({ provide: CATALOG_ITEM_STORAGE_SERVICE, useValue: new CatalogItemStorageService(userStorage) });

  const preloadedState: RootState = merge(
    {
      [favoritesReducerPath]: favoritesService.getFavorites(),
    },
    redux.preloadedState,
  );
  const store = configureStore({
    preloadedState,
    persistConfig: redux.persistConfig,
    container,
  });
  container.bind({ provide: STORE, useValue: store });

  const modalManager = new RefModalManager(eventBus as any);
  container.bind({ provide: MODAL_MANAGER, useValue: modalManager });
  container.bind({ provide: MODAL_STATS_PROVIDER, useValue: modalManager });

  container.bind({
    provide: INTENTION_STORAGE,
    useValue: isBrowser ? new JSONStorage<IntentData>(window.sessionStorage, 'intent') : new MemoryStorage<IntentData>(),
  });
  container.bind({ provide: INTENTION_SERVICE, useValue: new IntentionService(container.get(INTENTION_STORAGE), container.get(ROUTER)) });
  container.bind({
    provide: WEBSOCKET_MANAGER,
    useValue: isBrowser
      ? createSocketManager({ CORE_SERVER_WEBSOCKET_ORIGIN: configManager.getOrThrow('CORE_SERVER_WEBSOCKET_ORIGIN') })
      : { socket: () => ({ on: () => {}, connect: () => {}, disconnect: () => {} }) },
  });

  eventBus.subscribe(spiderService.onMessageReceived.bind(spiderService));
  if (configManager.get('DEBUG')) {
    isBrowser && eventBus.subscribe((e) => console.debug('[📧]', e[Symbol.toPrimitive] ? e[Symbol.toPrimitive]('default') : e));
    isBrowser && (window['eventBus'] = eventBus);
    isBrowser && (window['spider'] = spiderService.spider);
  }

  registerServiceWorker(configManager);

  return container;
}
